import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  serverTimestamp,
  Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../lib/firebase/app';
import { tryRefreshAuth, markAuthHealthy } from '../lib/firebase/auth';
import type { LocationDrop, LocationDropComment } from '../types/location';

const DROPS_COLLECTION = 'locationDrops';

/** Subscribe to active drops. Filters expired client-side to avoid index issues.
 *  Auto-retries on permission-denied (token refresh gap). */
export function subscribeDrops(
  onUpdate: (drops: LocationDrop[]) => void,
): Unsubscribe {
  let innerUnsub: Unsubscribe | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let cancelled = false;
  let retryCount = 0;

  function start() {
    const q = query(collection(db, DROPS_COLLECTION));
    innerUnsub = onSnapshot(q, (snap) => {
      retryCount = 0;
      if (!snap.metadata.fromCache) markAuthHealthy();
      const now = Date.now();
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() } as LocationDrop));
      const active = all.filter((d) => {
        if (!d.expiresAt?.toMillis) return false;
        return d.expiresAt.toMillis() > now;
      });
      console.log(`[Drops] Total: ${all.length}, Active: ${active.length}`);
      onUpdate(active);
    }, (err) => {
      console.warn('[Drops] Listener error:', err.code ?? err.message);
      if (!cancelled && retryCount < 5 && (err.code === 'permission-denied' || err.code === 'unauthenticated')) {
        const delay = Math.min(3000 * Math.pow(2, retryCount), 30000);
        retryCount++;
        retryTimer = setTimeout(async () => {
          if (cancelled) return;
          const refreshed = await tryRefreshAuth();
          if (!cancelled && refreshed) start();
        }, delay);
      }
    });
  }

  start();

  return () => {
    cancelled = true;
    if (retryTimer) clearTimeout(retryTimer);
    innerUnsub?.();
  };
}

/** Create a drop. Returns the drop ID. */
export async function createDrop(params: {
  authorId: string;
  authorNickname: string;
  authorProfileImageUrl?: string;
  authorGender?: 'male' | 'female' | 'unknown';
  title: string;
  content: string;
  lat: number;
  lng: number;
  cellsSpent: number;
  durationMinutes: number;
}): Promise<string> {
  const expiresAt = Timestamp.fromMillis(Date.now() + params.durationMinutes * 60 * 1000);
  const ref = await addDoc(collection(db, DROPS_COLLECTION), {
    authorId: params.authorId,
    authorNickname: params.authorNickname,
    authorProfileImageUrl: params.authorProfileImageUrl ?? null,
    authorGender: params.authorGender ?? 'unknown',
    title: params.title,
    content: params.content,
    lat: params.lat,
    lng: params.lng,
    cellsSpent: params.cellsSpent,
    expiresAt,
    createdAt: serverTimestamp(),
    commentCount: 0,
  });

  // Post notification message to chat
  await addDoc(collection(db, 'messages'), {
    text: `📍 ${params.title}`,
    senderId: params.authorId,
    senderNickname: params.authorNickname,
    gender: params.authorGender ?? 'unknown',
    profileImageUrl: params.authorProfileImageUrl ?? null,
    lat: params.lat,
    lng: params.lng,
    radius: 0,
    isBot: false,
    isNearbyNotification: true,
    isDropNotification: true,
    manualFreqApplied: false,
    timestamp: serverTimestamp(),
  });

  return ref.id;
}

/** Extend a drop's duration (add more cells). */
export async function extendDrop(dropId: string, additionalMinutes: number, additionalCells: number): Promise<void> {
  const dropRef = doc(db, DROPS_COLLECTION, dropId);
  const snapDoc = await getDoc(dropRef);
  if (!snapDoc.exists()) return;
  const currentExpires = snapDoc.data().expiresAt as Timestamp;
  const newExpires = Timestamp.fromMillis(currentExpires.toMillis() + additionalMinutes * 60 * 1000);
  await updateDoc(dropRef, {
    expiresAt: newExpires,
    cellsSpent: increment(additionalCells),
  });
}

/** Update drop title/content. */
export async function updateDrop(dropId: string, data: { title?: string; content?: string }): Promise<void> {
  await updateDoc(doc(db, DROPS_COLLECTION, dropId), data);
}

/** Calculate refund amount for a drop (excluding base cost). */
export async function calculateRefund(dropId: string, baseCost: number = 500): Promise<number> {
  const snap = await getDoc(doc(db, DROPS_COLLECTION, dropId));
  if (!snap.exists()) return 0;
  const data = snap.data();
  const expiresAt = (data.expiresAt as Timestamp).toMillis();
  const createdMs = data.createdAt?.toMillis?.() ?? (expiresAt - 300000);
  const totalMs = Math.max(expiresAt - createdMs, 1);
  const remainingMs = Math.max(0, expiresAt - Date.now());
  const timeCost = Math.max(0, data.cellsSpent - baseCost);
  return Math.floor(timeCost * (remainingMs / totalMs));
}

/** Delete a drop. Returns remaining cells to refund (base cost excluded). */
export async function deleteDrop(dropId: string, baseCost: number = 500): Promise<number> {
  const refund = await calculateRefund(dropId, baseCost);
  await deleteDoc(doc(db, DROPS_COLLECTION, dropId));
  return refund;
}

/** Add a comment to a drop. */
export async function addDropComment(dropId: string, data: {
  authorId: string;
  authorNickname: string;
  content: string;
}): Promise<void> {
  await addDoc(collection(db, DROPS_COLLECTION, dropId, 'comments'), {
    ...data,
    createdAt: serverTimestamp(),
  });
  await updateDoc(doc(db, DROPS_COLLECTION, dropId), { commentCount: increment(1) });
}

/** Get comments for a drop. */
export async function getDropComments(dropId: string): Promise<LocationDropComment[]> {
  const q = query(collection(db, DROPS_COLLECTION, dropId, 'comments'), orderBy('createdAt', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as LocationDropComment));
}
